import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import type { ResultsSliceState, Deal, Document, ResearchDoc, Expert, ResultsByTurnEntry } from "./types/redux";

/** Facts are opaque here; only factId matters for merging and lookup. */
interface FactRef {
  factId?: string;
}

interface SetResultsPayload {
  results?: Deal[];
  documents?: Document[];
  researchDocs?: ResearchDoc[];
  experts?: Expert[];
  facts?: unknown[];
  turnId?: string;
  inlineDealsFlag?: boolean;
}

/** Copy the incoming deals and order them by relevanceRank when any carry one. */
function sortDealsByRelevance(results: Deal[]): Deal[] {
  const processedResults = [...results];

  // Check for relevance rank to enable sorting
  const hasRelevanceRank = processedResults.some(
    (deal) => deal?.relevanceRank !== undefined && deal?.relevanceRank !== null
  );

  if (hasRelevanceRank) {
    processedResults.sort((a, b) => {
      const rankA = a.relevanceRank ?? Number.MAX_SAFE_INTEGER;
      const rankB = b.relevanceRank ?? Number.MAX_SAFE_INTEGER;
      return rankA - rankB;
    });
  }
  return processedResults;
}

/**
 * Merge documents into the registry so citations from prior turns can still resolve.
 * Normalize documentId on insertion so downstream consumers can rely on it.
 */
function mergeDocumentRegistry(registry: Document[], documents: Document[]): Document[] {
  const existingDocsMap = new Map<string, Document>();
  (registry || []).forEach((d) => {
    const docId = d.documentId || d.docId;
    if (docId) existingDocsMap.set(docId, d);
  });
  documents.forEach((d) => {
    const docId = d.documentId || d.docId;
    if (docId) {
      // Copy and normalize so documentId is always set, without mutating the payload
      const normalized = !d.documentId && d.docId ? { ...d, documentId: d.docId } : d;
      existingDocsMap.set(docId, normalized);
    }
  });
  return Array.from(existingDocsMap.values());
}

/** Phase 3: MERGE incoming facts with existing ones by factId to preserve history. */
function mergeFacts(existing: unknown[], incoming: unknown[]): unknown[] {
  // Build a map of existing facts by ID for efficient lookup
  const existingFactsMap = new Map<string, unknown>();
  existing.forEach((f) => {
    const { factId } = f as FactRef;
    if (factId) existingFactsMap.set(factId, f);
  });

  // Add new facts, updating existing ones
  incoming.forEach((f) => {
    const { factId } = f as FactRef;
    if (factId) existingFactsMap.set(factId, f);
  });

  return Array.from(existingFactsMap.values());
}

/**
 * This slice manages the state for the entire results panel, including the deals table
 * and facts.
 */
const initialState: ResultsSliceState = {
  deals: [],
  documents: [],
  documentRegistry: [], // Merged across turns so citations from prior turns can resolve
  facts: [], // Phase 3: Central source of truth for all facts
  status: "idle", // Represents the data fetching status: idle | loading | succeeded | failed
  error: null,
  resultsByTurn: {},
};


const resultsSlice = createSlice({
  name: "results",
  initialState,
  reducers: {
    /**
     * Sets the status to 'loading' without clearing previous results.
     * This preserves the workspace view while waiting for new results.
     */
    fetchResultsPending: (state) => {
      state.status = "loading";
      // Don't clear existing results - this keeps the workspace view active
      // Results will be replaced when the new response arrives
      state.error = null;
    },
    /**
     * Populates the state with data received from the backend.
     * It separates the main 'results' (for the deals table) from documents and facts.
     * @param {object} action.payload - The structured data from the backend,
     * containing 'results', 'documents', and 'facts' arrays.
     */

    setDocuments: (state, action: PayloadAction<Document[] | undefined>) => {
      const documents = action.payload || [];
      state.documents = documents;
    },

    clearDocuments: (state) => {
      state.documents = [];
    },

    setResults: (state, action: PayloadAction<SetResultsPayload>) => {
      const { results, documents, researchDocs, experts, facts, turnId, inlineDealsFlag } = action.payload;
      state.status = "succeeded";

      const processedResults: Deal[] = Array.isArray(results) ? sortDealsByRelevance(results) : [];

      // Only write top-level deals when flag is OFF (legacy behaviour)
      if (!inlineDealsFlag) {
        state.deals = processedResults;
      }

      // Write per-turn results when turnId is present (used by inline tables)
      if (turnId) {
        const turnDocs = documents || [];
        const turnResearchDocs = Array.isArray(researchDocs) ? researchDocs : [];
        state.resultsByTurn[turnId] = {
          deals: processedResults,
          documents: turnDocs,
          researchDocs: turnResearchDocs,
          // Callers that carry no Expert Finder output (legacy handler) omit the field
          experts: experts ?? [],
        };
      }

      // Store documents as simple array - replace on each turn (workspace context)
      const documentsArray = documents || [];
      state.documents = documentsArray;

      if (documentsArray.length > 0) {
        state.documentRegistry = mergeDocumentRegistry(state.documentRegistry, documentsArray);
      }

      if (Array.isArray(facts)) {
        state.facts = mergeFacts(state.facts, facts);
      }
    },
    /**
     * Sets the status to 'failed' and stores an error message.
     * This is dispatched when the API call or data processing fails.
     */
    fetchResultsRejected: (state, action: PayloadAction<string>) => {
      state.status = "failed";
      state.error = action.payload;
    },
    /**
     * Resets the slice to its initial state.
     * Phase 3: Clears all workspace data including facts, documents, and results
     */
    clearResults: (state) => {
      Object.assign(state, initialState);
    },
    /**
     * Reverts the status from 'loading' back to 'succeeded' without clearing data.
     * Used when a turn completes without sending a results_payload (e.g. viability check failed).
     * This keeps the previous deals/docs visible instead of showing loading skeletons.
     */
    revertLoadingToSucceeded: (state) => {
      if (state.status === "loading") {
        state.status = "succeeded";
      }
    },
    /**
     * Appends a single fact to the facts array (used for streaming facts individually).
     * If a fact with the same factId already exists, it will be updated.
     */
    appendFact: (state, action: PayloadAction<unknown>) => {
      const fact = action.payload as any;
      if (!fact || !fact.factId) {
        console.warn("⚠️ [ResultsSlice] appendFact called with invalid fact:", fact);
        return;
      }

      // Check if fact already exists
      const existingIndex = state.facts.findIndex((f: any) => f.factId === fact.factId);
      if (existingIndex >= 0) {
        // Update existing fact
        state.facts[existingIndex] = fact;
      } else {
        // Append new fact
        state.facts.push(fact);
      }
    },
  },
});

export const {
  fetchResultsPending,
  setResults,
  fetchResultsRejected,
  clearResults,
  setDocuments,
  clearDocuments,
  appendFact,
  revertLoadingToSucceeded,
} = resultsSlice.actions;

// Selector to detect if workspace has active results
export const selectHasWorkspaceResults = (state: any): boolean =>
  (state.resultsSlice?.deals?.length ?? 0) > 0 ||
  (state.resultsSlice?.documents?.length ?? 0) > 0;

// Phase 3: Selectors for fact lookups
/**
 * Select all facts from central state
 */
export const selectAllFacts = (state: any): unknown[] => state.resultsSlice?.facts || [];

/**
 * Select facts by an array of fact IDs
 * @param {string[]} factIds - Array of fact IDs to look up
 * @returns {object[]} Array of fact objects matching the provided IDs
 */
export const selectFactsByIds = (state: any, factIds: string[] | undefined): unknown[] => {
  if (!factIds || !Array.isArray(factIds)) return [];
  const allFacts = selectAllFacts(state);
  const factMap = new Map(allFacts.map((f: any) => [f.factId, f]));
  return factIds.map((id: string) => factMap.get(id)).filter(Boolean);
};

/**
 * Select a single fact by ID
 * @param {string} factId - The fact ID to look up
 * @returns {object|null} The fact object or null if not found
 */
export const selectFactById = (
  state: { resultsSlice?: Pick<ResultsSliceState, "facts"> },
  factId: string | undefined
): unknown => {
  if (!factId) return null;
  const allFacts = selectAllFacts(state);
  return allFacts.find((f) => (f as FactRef).factId === factId) || null;
};

/**
 * Select all facts for a specific document
 * @param {string} documentId - The document ID to filter by
 * @returns {object[]} Array of facts associated with the document
 */
export const selectFactsByDocumentId = (state: any, documentId: string | undefined): unknown[] => {
  if (!documentId) return [];
  const allFacts = selectAllFacts(state);
  return allFacts.filter((f: any) => f.documentId === documentId);
};

/**
 * Select a document by ID
 * @param {string} documentId - The document ID to look up
 * @returns {object|null} The document object or null if not found
 */
export const selectDocumentById = (state: any, documentId: string | undefined): Document | null => {
  if (!documentId) return null;
  const documents = state.resultsSlice?.documents || [];
  return documents.find((d: any) => d.documentId === documentId) || null;
};

/**
 * Select a document from the registry (merged across all turns).
 * Use this for citation resolution so facts from prior turns can still find their documents.
 */
export const selectDocumentFromRegistry = (state: any, documentId: string | undefined): Document | null => {
  if (!documentId) return null;
  const registry = state.resultsSlice?.documentRegistry || [];
  return registry.find((d: any) => d.documentId === documentId) || null;
};

/**
 * Select per-turn results (deals + documents + researchDocs) for a specific turnId.
 * Used by inline tables to render results tied to a specific chat message.
 */
export const selectResultsForTurn = (
  state: any,
  turnId: string | undefined
): ResultsByTurnEntry | undefined => {
  if (!turnId) return undefined;
  return state.resultsSlice?.resultsByTurn?.[turnId];
};

/**
 * Select research docs for a specific turnId. Returns an empty array when the
 * turn has no research docs or the turn is unknown, so consumers don't need
 * to null-check before mapping.
 *
 * Research docs arrive inside the same `documents[]` array as deal-tied docs
 * (the backend merges them there for citation resolution); the FE identifies
 * them via `source === "research_desk"` rather than a dedicated wire bucket.
 * If the backend ever emits a top-level `researchDocs[]` override, we prefer it.
 */
export const selectResearchDocsForTurn = (
  state: any,
  turnId: string | undefined
): ResearchDoc[] => {
  if (!turnId) return [];
  const entry = state.resultsSlice?.resultsByTurn?.[turnId];
  if (!entry) return [];
  if (Array.isArray(entry.researchDocs) && entry.researchDocs.length > 0) {
    return entry.researchDocs;
  }
  const docs = entry.documents ?? [];
  // Normalise docId from documentId on read. The drift guard accepts either
  // key on documents, and ResultsSlice.setResults already normalises for the
  // registry — but docs stored on resultsByTurn.documents aren't normalised
  // at insertion time, so a doc carrying only `documentId` would hit
  // `if (!doc.docId) return` in handleDocClick and silently no-op.
  return docs
    .filter((d: Document) => d && d.source === "research_desk")
    .map((d: Document) => {
      const docId = (d as ResearchDoc).docId ?? d.documentId;
      return docId && !d.docId ? { ...d, docId } : d;
    });
};

const NO_EXPERTS: Expert[] = Object.freeze([]) as unknown as Expert[];

/**
 * Select the experts for a turn. Returns the stored array reference (or a
 * shared frozen empty array) so useAppSelector sees a stable value between
 * renders — never build a fresh array here.
 */
export const selectExpertsForTurn = (
  state: { resultsSlice?: Pick<ResultsSliceState, "resultsByTurn"> },
  turnId: string | undefined
): Expert[] => {
  if (!turnId) return NO_EXPERTS;
  return state.resultsSlice?.resultsByTurn?.[turnId]?.experts ?? NO_EXPERTS;
};

export default resultsSlice.reducer;
